import { React, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import CustomInput from "../CustomInput/CustomInput";
import { useDispatch } from "react-redux";
import { LoginUser, socialLoginService } from "../../services/LoginUser";
import InputAdornment from "@mui/material/InputAdornment";
import { ReactComponent as EyeIcon } from '../../assets/svg/visible.svg';
import { ReactComponent as OffEyeIcon } from '../../assets/svg/invisible.svg';
import { ReactComponent as GoogleIcon } from '../../assets/svg/google-icon.svg';
import { setOpenLoginFlow, setOpenSignupFlow, setOpenAgentSignupFlow, setAgentSignupStepCount } from "../../redux/login";
import ForgetPassword from "../Dialog/ForgetPasswordModal";

const Login = () => {
  const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleGoogleSignIn = () => {
    try {
      dispatch(
        socialLoginService({
          params: {
            userType: 1,
          },
        }),
      );
    } catch (error) {
      console.error("Google sign-in error:", error);
    }
  };

  const [password, setPassword] = useState("");
  const [openForgetPwdDialog, setOpenForgetPwdDialog] = useState(false);
  const [type, setType] = useState('password');

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '' };
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    const label = score <= 1 ? 'Weak' : score === 2 ? 'Fair' : score === 3 ? 'Good' : 'Strong';
    return { score, label };
  };
  const handleToggle = () => {
    if (type === 'password') {
      //  setIcon(eye);
      setType('text')
    } else {
      //  setIcon(eyeOff)
      setType('password')
    }
  }
  const handleLogin = async (values, { resetForm }) => {
    try {
      let formData = new FormData(); //formdata object
      formData.append("email", values?.email); //append the values with key, value pair
      formData.append("password", values?.password);
      formData.append("remember_token", 1);
      const extraConfig = {
        headers: { "content-type": "multipart/form-data" },
      };

      const response = await dispatch(LoginUser(formData, extraConfig));

      if (response) {
        if (response.emailVerificationPending) {
          dispatch(setOpenLoginFlow(false));
          dispatch(setAgentSignupStepCount(4));
          dispatch(setOpenAgentSignupFlow(true));
          return;
        }

        // close login popup
        dispatch(setOpenLoginFlow(false));

        // 🔥 redirect logic based on what was stored before login
        const redirectRaw = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);

        if (redirectRaw) {
          try {
            const { path, state } = JSON.parse(redirectRaw) || {};
            // clear it so it doesn't get reused
            localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

            if (path) {
              if (state) {
                navigate(path, { state });
              } else {
                navigate(path);
              }
            } else {
              navigate(
                response?.data?.userDetail?.user_type === 2
                  ? "/agent-dashboard"
                  : "/buy-property"
              );
            }
          } catch (err) {
            console.error("Error parsing post-login redirect:", err);
            navigate(
              response?.data?.userDetail?.user_type === 2
                ? "/agent-dashboard"
                : "/buy-property"
            );
          }
        } else {
          // no redirect stored -> default
          navigate(
            response?.data?.userDetail?.user_type === 2
              ? "/agent-dashboard"
              : "/buy-property"
          );
        }
      }

      // reset the form
      resetForm({
        email: "",
        password: "",
      });
    } catch (e) {
      console.log(e, "err");
      resetForm({
        email: "",
        password: "",
      });
    }
  };


  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter Email")
        .required("Please enter Email"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .max(128, "Password cannot exceed 128 characters")
        .required("Please Enter your password"),
    }),
    onSubmit: handleLogin,
  });

  return (
    <>
      <ForgetPassword openForgetPwdDialog={openForgetPwdDialog} setOpenForgetPwdDialog={setOpenForgetPwdDialog} />
      <form onSubmit={formik.handleSubmit} noValidate>
        <Box className="login-form">
          {/* <Typography
            variant="lSize"
            component="h5"
            className="font-w-500 line-h-extended-1"
          >
            {t("LogIn.signIn")}
          </Typography> */}
          <Box className="login-form-inner">
            <CustomInput
              variant="outlined"
              fullWidth
              required
              label={t("LogIn.EmailAddress")}
              placeholder={t("LogIn.EmailAddress")}
              onChange={formik.handleChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                classes: {
                  notchedOutline: "rounded",
                },
              }}
              value={formik.values.email}
              name="email"
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <CustomInput type={type}
              variant="outlined"
              fullWidth
              required
              label={t("LogIn.Password")}
              placeholder={t("LogIn.Password")}
              onChange={(e) => { formik.handleChange(e); setPassword(e.target.value) }}
              InputLabelProps={{ shrink: true }}
              InputProps={{

                endAdornment: (
                  <InputAdornment position="end" onClick={handleToggle} sx={{ width: '25px', height: "25px", cursor: 'pointer' }}>
                    {type == 'password' && <OffEyeIcon style={{ fill: '#0085FD', height: '26px', width: '26px' }} />}
                    {type == 'text' && <EyeIcon style={{ fill: '#0085FD', height: '26px', width: '26px' }} />}
                  </InputAdornment>
                ),
                classes: {
                  notchedOutline: "rounded",
                },
              }}
              value={formik.values.password}
              name="password"
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />

            {(() => {
              const strength = getPasswordStrength(password);
              return password ? (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ height: 6, background: '#E6E6E6', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%',
                      width: `${(strength.score / 4) * 100}%`,
                      background: strength.score <= 1 ? '#ff4d4f' : strength.score === 2 ? '#faad14' : strength.score === 3 ? '#52c41a' : '#237804',
                      borderRadius: 2,
                      transition: 'width .2s ease'
                    }} />
                  </Box>
                  <Typography variant="xxsSize" className="font-w-400 gray-color" sx={{ mt: 0.5 }}>{strength.label}</Typography>
                </Box>
              ) : null;
            })()}

            <Button
              sx={{
                bgcolor: " #0085FD",
                color: "white",
                padding: "8px 50px",
                borderRadius: "10px",
                border: "none",
                // marginLeft: "160px",
                marginTop: "24px",
                width: '100%',
                "&:hover": {
                  bgcolor: " #0085FD",
                },
              }}
              variant="outlined"
              type="submit"
              className="font-w-400 signInBtn"
            >
              {t("LogIn.signIn")}
            </Button>
            <Box
              sx={{
                width: "100%",
                textAlign: "center",
                textDecoration: "underline",
              }}
              className="black-color-2"
              component="div"
            >
              <Link to="/" >
                <Typography
                  variant="xxsSize"
                  color="#111111"
                  className="font-w-400"
                  onClick={() => {
                    localStorage.setItem('role', 'user')
                    setOpenForgetPwdDialog(true);
                  }}
                >
                  {t("LogIn.forgotYourPassword")}
                </Typography>
              </Link>
            </Box>
          </Box>
          <Box
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              margin: "0 40px",
            }}
            component="div"
            className="sign-in-btn-container"
          >
            <Typography color="#111111" variant="xxsSize">
              {" "}
              {t("LogIn.DontHaveAnAccount")}{" "}
              <Typography
                component="span"
                color="#111111"
                onClick={() => {
                  dispatch(setOpenLoginFlow(false));
                  dispatch(setOpenSignupFlow(true));
                }}
                sx={{ textDecoration: "underline", cursor: "pointer" }}
              >
                {t("LogIn.SignUp")}
              </Typography>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", margin: "20px 40px" }}>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "#D9D9D9" }} />
            <Typography sx={{ margin: "0 12px", color: "#6B7280" }} variant="xsSize">
              {t("LogIn.OR") || "OR"}
            </Typography>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "#D9D9D9" }} />
          </Box>

          <Box
            sx={{ display: "grid", width: "100%", marginBottom: "32px", padding: "0 40px" }}
            component="div"
          >
            <Button
              variant="outlined"
              onClick={handleGoogleSignIn}
              sx={{
                borderRadius: "12px",
                padding: "12px 16px",
                borderColor: "#D0D5DD",
                color: "#111111",
                textTransform: "none",
                fontWeight: 600,
                display: "flex",
                gap: 1.5,
                "&:hover": {
                  borderColor: "#0085FD",
                  backgroundColor: "#F8FBFF",
                },
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: "1px solid #D0D5DD",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <GoogleIcon width={16} height={16} />
              </Box>
              <Typography
                variant="xxsSize"
                component="span"
                sx={{ color: "#111111", fontWeight: 500 }}
              >
                Continue with Google
              </Typography>
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};
export default Login;
